/**
 * The F3 overlay's numbers (62.20 follow-up, lane C): the owner's own machine measures drawing on a real GPU,
 * since the headless profile (sweeps/2026-10-10-l0-frame-profile.md) draws on the CPU. Pure: the profiler
 * collects samples, this summarises them.
 *
 * - **Frame interval**: requestAnimationFrame to requestAnimationFrame — the frame's real cost, GPU waits and
 *   the browser's work included (the JS time alone misses both).
 * - **Simulation**: time inside the match's advance per frame, and the ticks it ran.
 * - **Clock**: simulated seconds over wall seconds. The host clamps a frame's dt to 50 ms, so frames slower than
 *   20 a second slow the battle rather than skip time: below 1× is that clamp at work.
 * - **GC**: a page cannot see collections; Chrome's heap counter (performance.memory) dropping marks one.
 */

export interface FrameSamples {
  /** rAF-to-rAF intervals, ms. */
  intervals: number[];
  /** JS work per frame (animate's own time), ms. */
  work: number[];
  /** The simulation's time inside each frame, ms (absent on a frame without one). */
  sim: number[];
  /** Ticks run per frame. */
  ticks: number[];
  /** Simulated seconds advanced over the window. */
  simSeconds: number;
  /** Chrome's used JS heap after each frame, bytes (empty where the browser has no performance.memory). */
  heap: number[];
}

export interface FrameSummary {
  fps: number;
  interval: { p50: number; p95: number; max: number };
  work: { mean: number; max: number };
  sim: { mean: number; max: number; ticksPerFrame: number } | null;
  /** Simulated time per wall time (1 = real time). */
  clockRate: number | null;
  /** 65.38: the share of frames longer than the host's dt clamp (FRAME_CLAMP_MS): each of them slowed the battle. */
  overClamp: number;
  heap: { mb: number; drops: number; freedMb: number } | null;
}

const pct = (xs: readonly number[], p: number): number => {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(p * s.length))];
};
const mean = (xs: readonly number[]) => xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;

/** The host's clamp on a frame's dt (main.ts animate), ms: a longer frame advances the battle by this much only. */
export const FRAME_CLAMP_MS = 50;

/** A heap drop of at least this much between frames counts as a collection. */
export const GC_DROP_BYTES = 512 * 1024;

export function summariseFrames(f: FrameSamples): FrameSummary {
  const wall = f.intervals.reduce((a, b) => a + b, 0) / 1000;
  let drops = 0, freed = 0;
  for (let i = 1; i < f.heap.length; i++) {
    const d = f.heap[i - 1] - f.heap[i];
    if (d >= GC_DROP_BYTES) { drops++; freed += d; }
  }
  return {
    fps: wall > 0 ? f.intervals.length / wall : 0,
    interval: { p50: pct(f.intervals, 0.5), p95: pct(f.intervals, 0.95), max: f.intervals.length ? Math.max(...f.intervals) : 0 },
    work: { mean: mean(f.work), max: f.work.length ? Math.max(...f.work) : 0 },
    sim: f.sim.length ? { mean: mean(f.sim), max: Math.max(...f.sim), ticksPerFrame: mean(f.ticks) } : null,
    clockRate: f.sim.length && wall > 0 ? f.simSeconds / wall : null,
    overClamp: f.intervals.length ? f.intervals.filter(i => i > FRAME_CLAMP_MS).length / f.intervals.length : 0,
    heap: f.heap.length ? { mb: f.heap[f.heap.length - 1] / 1e6, drops, freedMb: freed / 1e6 } : null,
  };
}

/** The overlay's lines for a summary. */
export function frameSummaryLines(s: FrameSummary, frames: number): string[] {
  const lines = [
    `FRAME  ${s.fps.toFixed(0)} fps  interval p50 ${s.interval.p50.toFixed(1)}  p95 ${s.interval.p95.toFixed(1)}  max ${s.interval.max.toFixed(1)} ms  (${frames} frames)`,
    `JS     ${s.work.mean.toFixed(1)} ms a frame  max ${s.work.max.toFixed(1)}`,
  ];
  if (s.sim) lines.push(`SIM    ${s.sim.mean.toFixed(2)} ms a frame  max ${s.sim.max.toFixed(1)}  ${s.sim.ticksPerFrame.toFixed(1)} ticks a frame`);
  if (s.clockRate !== null) {
    lines.push(`CLOCK  ${s.clockRate.toFixed(2)}× real time${s.clockRate < 0.98 ? `  — ${Math.round(s.overClamp * 100)} % of frames over ${FRAME_CLAMP_MS} ms, each slowing the battle (the dt clamp)` : ''}`);
  }
  if (s.heap) lines.push(`HEAP   ${s.heap.mb.toFixed(0)} MB  GC ~${s.heap.drops} (${s.heap.freedMb.toFixed(0)} MB freed; Chrome's heap counter)`);
  return lines;
}
